import type { CalendarItem } from '@st/contract';

export interface CalendarDay {
  day: string;
  inMonth: boolean;
  isToday: boolean;
  items: CalendarItem[];
}

/** Prvi dan mjeseca u ISO obliku (`YYYY-MM-01`). */
export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function shiftMonth(monthIso: string, delta: number): string {
  const year = Number(monthIso.slice(0, 4));
  const month = Number(monthIso.slice(5, 7)) - 1 + delta;
  const d = new Date(Date.UTC(year, month, 1));
  return d.toISOString().slice(0, 10);
}

export function monthEnd(monthIso: string): string {
  const year = Number(monthIso.slice(0, 4));
  const month = Number(monthIso.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

/**
 * Raspon koji pokriva cijelu mrežu, ne samo mjesec — ponedjeljak prije prvog i
 * nedjelja poslije zadnjeg. Bez toga bi rubni tjedni bili prazni iako u njima
 * ima treninga.
 */
export function gridRange(monthIso: string): { from: string; to: string } {
  const first = new Date(`${monthStart(monthIso)}T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7; // ponedjeljak = 0
  const from = new Date(first);
  from.setUTCDate(from.getUTCDate() - lead);

  const last = new Date(`${monthEnd(monthIso)}T00:00:00Z`);
  const trail = 6 - ((last.getUTCDay() + 6) % 7);
  const to = new Date(last);
  to.setUTCDate(to.getUTCDate() + trail);

  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

/** Mreža 7×N s pridruženim stavkama. Tjedan počinje ponedjeljkom (hr i en). */
export function monthGrid(
  monthIso: string,
  items: readonly CalendarItem[],
  today: string,
): CalendarDay[] {
  const { from, to } = gridRange(monthIso);
  const prefix = monthIso.slice(0, 7);
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const list = byDay.get(item.day);
    if (list) list.push(item);
    else byDay.set(item.day, [item]);
  }

  const days: CalendarDay[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (cursor <= end) {
    const day = cursor.toISOString().slice(0, 10);
    days.push({
      day,
      inMonth: day.startsWith(prefix),
      isToday: day === today,
      items: byDay.get(day) ?? [],
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export type CalendarItemState = 'done' | 'missed' | 'upcoming' | 'event' | 'planned';

/**
 * Vizualno stanje stavke. Termin je `event` — nema odrađen/propušten — osim
 * termina koji je klijent sam zakazao: taj se mora razlikovati od propisanog
 * rasporeda jer ga klijent smije i premjestiti.
 */
export function itemState(item: CalendarItem): CalendarItemState {
  if (item.kind === 'event') return item.type === PLANNED_WORKOUT_TYPE ? 'planned' : 'event';
  if (item.completed) return 'done';
  if (item.missed) return 'missed';
  return 'upcoming';
}

/**
 * Termin koji je klijent sam zakazao ima vlastiti tip — propisani raspored
 * dolazi iz programa i mijenja se s njim, a ovo je klijentova namjera.
 * Zato se u kalendaru i prikazuje drukčije od propisanog treninga.
 */
export const PLANNED_WORKOUT_TYPE = 'planned_workout';

export function isPlannedWorkout(item: CalendarItem): boolean {
  return item.kind === 'event' && item.type === PLANNED_WORKOUT_TYPE;
}

/** Sastavlja ISO trenutak iz odvojenog polja za datum i vrijeme. */
export function toStartsAt(day: string, time: string): string | null {
  if (!day || !time) return null;
  const d = new Date(`${day}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Rastavlja ISO trenutak natrag u `<input type="date">` i `<input type="time">`. */
export function fromStartsAt(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    day: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
